/**
 * Design-system tokens, mirrored from the engineering handbook
 * (`docs/design-system/web/hybrid-3c.md` — "3c Hybrid" v3, the current
 * direction for first-party internal / back-office tools). Do not invent a
 * colour, radius or shadow outside this file: every component reads from
 * here, never a hardcoded hex.
 */

/** Brand teal — unchanged by the v3 warm-neutral migration (v3.1). */
export const brandColor = {
  signalTeal: '#0BA5B7',
  deepTeal: '#0E7C8A',
  brandTeal: '#55C3D2',
  tealPale: '#CFF2F6',
  surface: '#FFFFFF',
} as const;

/**
 * v3.1 warm-neutral palette — post-cooldown values (rev. v3.1a, 2026-07-20)
 * plus the rail bump to `#256B7E` (rev. v3.1b, 2026-07-22). The rail is a
 * *surface*, not the brand accent — distinct from both `brandColor.deepTeal`
 * and `brandColor.signalTeal`.
 */
export const warmNeutral = {
  canvas: '#F7F5F1',
  paper: '#FFFFFF',
  appbar: '#FBF9F5',
  rail: '#256B7E',
  ink: '#282624',
  textSecondary: '#63605A',
  divider: '#D9D3C8',
  grey: {
    50: '#F7F5F1',
    100: '#EEEAE3',
    200: '#DFDBD1',
    300: '#CDC7BB',
    400: '#A19A8D',
    500: '#77726A',
    600: '#57534C',
    700: '#3C3934',
    800: '#2C2926',
    900: '#282624',
  },
} as const;

/** v3.1 status colors — unchanged by the warm-neutral cooldown. */
export const statusColor = {
  success: '#16A34A',
  warning: '#FB923C',
  /** "Salmon" — inline field-level validation tint (distinct from `danger`). */
  error: '#FB7185',
  info: '#3B82F6',
  /** Canonical destructive-action red, kept as a standalone constant rather
   *  than folded into `error` (see `statusColor.error` doc-comment). */
  danger: '#EF4444',
} as const;

/**
 * v3.2 outline system — a thin dark border over a light same-hue fill, not a
 * thick line. `borderStrong` drives the datagrid header line and neutral
 * chips; `borderRow` is the soft in-table row separator; `borderCard`
 * (rev. v3.2a) is the light sand card border paired with the warm card
 * shadow.
 */
export const outline = {
  borderStrong: '#57534C',
  borderRow: '#D9D3C8',
  borderCard: '#D6C6A8',
} as const;

/**
 * v3.8 solid-button fill — a dark teal-ink, distinct from Signal Teal, so
 * filled CTAs don't clash with the teal rail/app-bar chrome. `primary.main`
 * still drives links, focus and the active menu item.
 */
export const cta = {
  fill: '#0B5062',
  fillHover: '#0A4552',
} as const;

/** v3 micro-tones not modeled by MUI's two-tier text.primary/secondary. */
export const customText = {
  tertiary: '#746E63',
  label: '#837868',
  idMono: '#A19A8D',
} as const;

/** v3.8bis — neutral user-avatar disc (not a teal-ink disc). */
export const userAvatarColor = {
  bg: '#EEEAE3',
  color: '#77726A',
} as const;

/**
 * v3.4 unified datagrid header — the single source of truth spread into both
 * the `RaDatagrid` override and every shell datagrid; never restyle a table
 * header locally.
 */
export const tableToken = {
  headerBg: '#FAF8F3',
  headCell: {
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: 1.3,
    textTransform: 'none' as const,
    color: warmNeutral.ink,
  },
} as const;

export const radius = {
  xs: 4,
  sm: 6,
  /** v3.8 button radius — rounded, not pill. */
  medium: 7,
  md: 12,
  pill: 999,
} as const;

/** v3.1 / v3.2a warm-retinted shadows (replaces the previous cool-grey ones). */
export const shadow = {
  card: '0 6px 16px -12px rgba(63,49,25,.35)',
  subtle: '0 1px 2px rgba(80,60,25,0.04), 0 4px 16px rgba(80,60,25,0.05)',
  popover: '0 4px 12px rgba(80,60,25,0.10), 0 12px 32px rgba(80,60,25,0.08)',
} as const;

/** v3.1 global scrollbar tones. */
export const scrollbar = {
  track: '#f3ece0',
  thumb: '#d8cdb8',
  thumbHover: '#a79c8b',
} as const;

export const spacingUnit = 8;

export const fontFamily = "'Inter', system-ui, -apple-system, sans-serif";

/** Mono type for identifiers/codes/technical metadata. */
export const fontFamilyMono = 'ui-monospace, SFMono-Regular, Menlo, monospace';

/**
 * App-bar/lockup two-tone brand colours. v3.7 moves the brand text lockup
 * to the top of the rail (white-on-teal) and drops the app-bar wordmark
 * entirely; these tokens remain only for the pre-authentication shell pages
 * (`AccessUnavailablePage`, `NoSessionPage`, `NotEntitledPage`,
 * `CustomAppBar`) that render on the canvas, not the rail, and are migrated
 * in a later wave.
 */
export const wordmark = {
  brand: brandColor.deepTeal,
  product: warmNeutral.textSecondary,
} as const;
